import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

export const CopyFulfillmentGroupViewsCard = memo(
  function CopyFulfillmentGroupViewsCard() {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const team = useRequiredContext(TeamContext);

    const [sourceTeamId, setSourceTeamId] = useState<string>("");
    const [destinationTeamId, setDestinationTeamId] = useState<string>(
      team._id.toString(),
    );
    const [showWarningModal, setShowWarningModal] = useState(false);

    const [copyViewsLoad, copyViews] = useTriggerLoad(async () => {
      const result = await rpcClient.copyFulfillmentGroupViews({
        sourceTeamId,
        destinationTeamId,
      });

      if (result.success) {
        toast(result.message ?? "Views copied successfully", {
          variant: "success",
        });
        setSourceTeamId("");
        setShowWarningModal(false);
      } else {
        toast(result.message ?? "Failed to copy views", { variant: "error" });
      }

      return result;
    });

    const handleOpenModal = useHandler(() => {
      if (!sourceTeamId || !destinationTeamId) {
        toast("Please enter both source and destination team IDs", {
          variant: "error",
        });
        return;
      }
      setShowWarningModal(true);
    });

    const handleConfirm = useHandler(async () => {
      await copyViews();
    });

    const handleCloseModal = useHandler(() => {
      if (!copyViewsLoad.pending) {
        setShowWarningModal(false);
      }
    });

    return (
      <>
        <Card
          subtitle="Copy fulfillment-group views from one team to another. This will delete all existing views on the destination team."
          title="Copy Fulfillment Group Views"
        >
          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Source Team ID
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                Views will be copied FROM this team
              </Text>
              <RedoTextInput
                placeholder="Enter source team ID"
                setValue={setSourceTeamId}
                value={sourceTeamId}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Destination Team ID
              </Text>
              <Text fontSize="xs" textColor="error">
                Views on this team will be DELETED and overwritten
              </Text>
              <RedoTextInput
                placeholder="Enter destination team ID"
                setValue={setDestinationTeamId}
                value={destinationTeamId}
              />
            </Flex>

            <RedoButton
              disabled={!sourceTeamId || !destinationTeamId}
              hierarchy="primary"
              onClick={handleOpenModal}
              text="Copy Views"
              theme="normal"
            />
          </Flex>
        </Card>

        <RedoModal
          buttonPlacement="full"
          isOpen={showWarningModal}
          modalSize="sm"
          onModalCloseRequested={handleCloseModal}
          primaryButton={{
            text: "Yes, copy views",
            onClick: handleConfirm,
            disabled: copyViewsLoad.pending,
            pending: copyViewsLoad.pending,
            theme: "destructive",
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: handleCloseModal,
            disabled: copyViewsLoad.pending,
          }}
          subtitle="This action cannot be undone."
          theme="warn"
          title="Copy Fulfillment Group Views?"
          TitleIcon={AlertTriangleIcon}
        >
          <Flex dir="column" gap="md">
            <Text fontSize="sm" textColor="secondary">
              You are about to copy all fulfillment-group views from the source
              team to the destination team.
            </Text>
            <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
              This action will:
            </Text>
            <Flex dir="column" gap="xs" style={{ paddingLeft: "20px" }}>
              <Text fontSize="xs" textColor="tertiary">
                • Delete ALL existing fulfillment-group views on the destination
                team
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                • Copy all views from the source team to the destination team
              </Text>
            </Flex>
            <Flex
              dir="column"
              gap="xs"
              mt="md"
              p="md"
              radius="md"
              style={{ backgroundColor: "var(--redo-colors-warning-50)" }}
            >
              <Text fontSize="xs" fontWeight="semibold" textColor="warning">
                Source Team: {sourceTeamId}
              </Text>
              <Text fontSize="xs" fontWeight="semibold" textColor="warning">
                Destination Team: {destinationTeamId}
              </Text>
            </Flex>
          </Flex>
        </RedoModal>
      </>
    );
  },
);
